import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { devicesApi } from '../services/devicesApi';

// Must match `LAST_DEVICE_CONTROL_ROUTE_KEY` in `DeviceControl.tsx`
const LAST_DEVICE_CONTROL_ROUTE_KEY = 'ui.last_device_control_route_id';

// Must match the key used by `src/components/Layout.tsx`
const LAST_CONTEXT_KEY = 'edgentik:lastNavContext:v1';

function looksLikeDeviceRouteUuid(value: string): boolean {
  const v = String(value || '').trim();
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(v);
}

function readLastControlRouteDeviceId(): string {
  try {
    const raw = localStorage.getItem(LAST_DEVICE_CONTROL_ROUTE_KEY);
    const v = String(raw || '').trim();
    return looksLikeDeviceRouteUuid(v) ? v : '';
  } catch {
    return '';
  }
}

type NavContext = {
  device_id?: string;
};

function readNavContext(): NavContext {
  try {
    const raw = localStorage.getItem(LAST_CONTEXT_KEY);
    if (!raw) return {};
    const v = JSON.parse(raw);
    if (!v || typeof v !== 'object') return {};
    const out: NavContext = {};
    if (typeof (v as any).device_id === 'string' && (v as any).device_id) out.device_id = (v as any).device_id;
    return out;
  } catch {
    return {};
  }
}

function clearNavDeviceId() {
  try {
    const raw = localStorage.getItem(LAST_CONTEXT_KEY);
    if (!raw) return;
    const v = JSON.parse(raw);
    if (!v || typeof v !== 'object') return;
    delete (v as any).device_id;
    localStorage.setItem(LAST_CONTEXT_KEY, JSON.stringify(v));
  } catch {
    // ignore
  }
}

const DeviceControlLauncher: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    (async () => {
      const ctx = readNavContext();
      const ctxDevice = String(ctx.device_id || '').trim();

      // `Layout` stores the `/devices/:id/control` path segment in `device_id`; that is usually the cloud
      // device row UUID, not the edge `device_id` string. Navigate immediately without `listDevices()`.
      if (looksLikeDeviceRouteUuid(ctxDevice)) {
        navigate(`/devices/${encodeURIComponent(ctxDevice)}/control`, { replace: true });
        return;
      }

      if (ctxDevice) {
        try {
          const devices = await devicesApi.listDevices();
          const match = devices.find((d) => d.device_id === ctxDevice || d.id === ctxDevice);
          if (!mounted) return;
          if (match) {
            navigate(`/devices/${encodeURIComponent(match.id)}/control`, { replace: true });
            return;
          }
        } catch {
          // Fall through to last-route / devices list fallback.
        }
      }

      const lastRoute = readLastControlRouteDeviceId();
      if (lastRoute) {
        navigate(`/devices/${encodeURIComponent(lastRoute)}/control`, { replace: true });
        return;
      }

      if (ctxDevice) clearNavDeviceId();
      if (mounted) navigate('/devices', { replace: true });
    })();

    return () => {
      mounted = false;
    };
  }, [navigate]);

  return (
    <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
      <CircularProgress size={18} />
      <Typography variant="body2" color="text.secondary">
        Opening Device Control…
      </Typography>
    </Box>
  );
};

export default DeviceControlLauncher;




